<template>
  <div>
    <div class="main-content fe-tracking-page">
      <div class="container py-4 py-lg-5">
        <header class="fe-tracking-page__header">
          <div class="fe-tracking-page__icon-wrap">
            <i class="fas fa-shipping-fast"></i>
          </div>
          <h1 class="fe-tracking-page__title">Track Your Order</h1>
          <p class="fe-tracking-page__subtitle">
            Enter your order reference number to see the real-time status of your order.
          </p>
        </header>

        <div class="row g-4 align-items-start">
          <div v-if="isLoggedIn" class="col-lg-3 col-md-4">
            <account-sidebar />
          </div>
          <div :class="isLoggedIn ? 'col-lg-9 col-md-8' : 'col-12'">

        <div class="fe-tracking-search-card">
          <form @submit.prevent="handleTrack" class="fe-tracking-search">
            <div class="fe-tracking-search__input-wrap">
              <i class="fas fa-search fe-tracking-search__icon"></i>
              <input
                v-model="searchRef"
                type="text"
                class="fe-tracking-search__input"
                placeholder="Enter order reference (e.g. ORD-682037A1B)"
                :disabled="loading"
              />
            </div>
            <button
              type="submit"
              class="fe-tracking-search__btn"
              :disabled="loading || !searchRef.trim()"
            >
              <span v-if="loading" class="fe-tracking-search__spinner"></span>
              <span v-else>Track Order</span>
            </button>
          </form>
        </div>

        <div v-if="isLoggedIn && !trackingData && !error && !loading" class="fe-tracking-user-orders">
          <div class="fe-tracking-user-orders__head">
            <h2 class="fe-tracking-user-orders__title">
              <i class="fas fa-box-open"></i> Your Recent Orders
            </h2>
            <span v-if="userOrders.length" class="fe-tracking-user-orders__count">
              {{ userOrders.length }} order(s)
            </span>
          </div>

          <div v-if="userOrdersLoading" class="fe-tracking-state">
            <div class="fe-tracking-spinner"></div>
            <p>Loading your orders...</p>
          </div>

          <div v-else-if="userOrders.length === 0" class="fe-tracking-empty">
            <i class="fas fa-inbox"></i>
            <p>No orders found.</p>
            <router-link to="/shop" class="fe-tracking-empty__link">Start Shopping</router-link>
          </div>

          <div v-else class="fe-tracking-orders-grid">
            <div
              v-for="o in userOrders"
              :key="o.order.id"
              class="fe-tracking-order-card"
              @click="selectOrder(o)"
            >
              <div class="fe-tracking-order-card__top">
                <div>
                  <span class="fe-tracking-order-card__ref">{{ o.order.reference }}</span>
                  <span class="fe-tracking-order-card__date">{{ formatDate(o.order.date) }}</span>
                </div>
                <span class="fe-tracking-order-card__status" :class="getStatusClass(o.current_step, o.timeline)">
                  {{ getCurrentStatusLabel(o.current_step, o.timeline) }}
                </span>
              </div>
              <div class="fe-tracking-order-card__progress">
                <div class="fe-tracking-mini-progress">
                  <div
                    v-for="step in 5"
                    :key="step"
                    class="fe-tracking-mini-progress__dot"
                    :class="{
                      'is-completed': step <= o.current_step,
                      'is-failed': o.timeline.some(t => t.status === 'failed')
                    }"
                  ></div>
                  <div
                    class="fe-tracking-mini-progress__bar"
                    :style="{ width: getMiniProgressWidth(o) }"
                  ></div>
                </div>
              </div>
              <div class="fe-tracking-order-card__bottom">
                <span class="fe-tracking-order-card__total">
                  LKR {{ formatMoney(o.order.total_order_value) }}
                </span>
                <span class="fe-tracking-order-card__arrow">
                  <i class="fas fa-chevron-right"></i>
                </span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="error && !loading" class="fe-tracking-error" role="alert">
          <div class="fe-tracking-error__icon">
            <i class="fas fa-exclamation-triangle"></i>
          </div>
          <div>
            <h3 class="fe-tracking-error__title">Order Not Found</h3>
            <p class="fe-tracking-error__text">{{ error }}</p>
          </div>
        </div>

        <div v-if="trackingData && !loading" class="fe-tracking-result">
          <button
            v-if="isLoggedIn"
            type="button"
            class="fe-tracking-back-btn"
            @click="clearTracking"
          >
            <i class="fas fa-arrow-left"></i> Back to all orders
          </button>

          <div class="fe-tracking-result__header">
            <div class="fe-tracking-result__info">
              <div class="fe-tracking-result__ref-row">
                <span class="fe-tracking-result__label">Order Reference</span>
                <h2 class="fe-tracking-result__ref">{{ trackingData.order.reference }}</h2>
              </div>
              <div class="fe-tracking-result__meta">
                <div class="fe-tracking-result__meta-item">
                  <i class="far fa-calendar-alt"></i>
                  <span>{{ formatDate(trackingData.order.date) }}</span>
                </div>
                <div class="fe-tracking-result__meta-item">
                  <i class="fas fa-rupee-sign"></i>
                  <span>LKR {{ formatMoney(trackingData.order.total_order_value) }}</span>
                </div>
                <div v-if="trackingData.order.total_item_qty" class="fe-tracking-result__meta-item">
                  <i class="fas fa-cubes"></i>
                  <span>{{ trackingData.order.total_item_qty }} item(s)</span>
                </div>
              </div>
            </div>
            <div class="fe-tracking-result__status-badge" :class="getMainStatusClass()">
              <i :class="'fas ' + getMainStatusIcon()"></i>
              {{ getMainStatusText() }}
            </div>
          </div>

          <div class="fe-tracking-timeline-card">
            <h3 class="fe-tracking-timeline-card__title">
              <i class="fas fa-route"></i> Tracking Timeline
            </h3>
            <div class="fe-tracking-timeline">
              <div
                v-for="(step, idx) in trackingData.timeline"
                :key="step.key"
                class="fe-tracking-timeline__step"
                :class="{
                  'is-completed': step.status === 'completed',
                  'is-active': step.status === 'in_progress',
                  'is-failed': step.status === 'failed',
                  'is-pending': step.status === 'pending',
                }"
              >
                <div class="fe-tracking-timeline__connector">
                  <div
                    v-if="idx > 0"
                    class="fe-tracking-timeline__line"
                    :class="{
                      'is-filled': step.status === 'completed' || step.status === 'failed',
                    }"
                  ></div>
                  <div class="fe-tracking-timeline__dot">
                    <i v-if="step.status === 'completed'" class="fas fa-check"></i>
                    <i v-else-if="step.status === 'failed'" class="fas fa-times"></i>
                    <i v-else-if="step.status === 'in_progress'" class="fas fa-spinner fa-pulse"></i>
                    <span v-else class="fe-tracking-timeline__dot-num">{{ step.step }}</span>
                  </div>
                  <div
                    v-if="idx < trackingData.timeline.length - 1"
                    class="fe-tracking-timeline__line fe-tracking-timeline__line--bottom"
                    :class="{
                      'is-filled': step.status === 'completed' && trackingData.timeline[idx + 1].status !== 'pending',
                    }"
                  ></div>
                </div>
                <div class="fe-tracking-timeline__content">
                  <div class="fe-tracking-timeline__icon-bg">
                    <i :class="'fas ' + step.icon"></i>
                  </div>
                  <div class="fe-tracking-timeline__text">
                    <h4 class="fe-tracking-timeline__title">{{ step.title }}</h4>
                    <p class="fe-tracking-timeline__desc">{{ step.description }}</p>
                    <span v-if="step.date" class="fe-tracking-timeline__date">
                      <i class="far fa-clock"></i> {{ formatDateTime(step.date) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="fe-tracking-details-row">
            <div v-if="trackingData.delivery" class="fe-tracking-info-card">
              <h3 class="fe-tracking-info-card__title">
                <i class="fas fa-truck"></i> Delivery Information
              </h3>
              <div class="fe-tracking-info-card__grid">
                <div class="fe-tracking-info-card__item">
                  <span class="fe-tracking-info-card__label">Delivery No</span>
                  <span class="fe-tracking-info-card__value">{{ trackingData.delivery.delivery_no || '—' }}</span>
                </div>
                <div class="fe-tracking-info-card__item">
                  <span class="fe-tracking-info-card__label">Status</span>
                  <span class="fe-tracking-info-card__value fe-tracking-info-card__value--status">
                    {{ trackingData.delivery.status }}
                  </span>
                </div>
                <div v-if="trackingData.delivery.driver_name" class="fe-tracking-info-card__item">
                  <span class="fe-tracking-info-card__label">Driver</span>
                  <span class="fe-tracking-info-card__value">{{ trackingData.delivery.driver_name }}</span>
                </div>
                <div v-if="trackingData.delivery.scheduled_date" class="fe-tracking-info-card__item">
                  <span class="fe-tracking-info-card__label">Scheduled Date</span>
                  <span class="fe-tracking-info-card__value">{{ formatDate(trackingData.delivery.scheduled_date) }}</span>
                </div>
                <div v-if="trackingData.delivery.delivered_at" class="fe-tracking-info-card__item">
                  <span class="fe-tracking-info-card__label">Delivered At</span>
                  <span class="fe-tracking-info-card__value">{{ formatDateTime(trackingData.delivery.delivered_at) }}</span>
                </div>
                <div v-if="trackingData.delivery.city" class="fe-tracking-info-card__item">
                  <span class="fe-tracking-info-card__label">City</span>
                  <span class="fe-tracking-info-card__value">{{ trackingData.delivery.city }}</span>
                </div>
              </div>
            </div>

            <div v-if="trackingData.shipping_address" class="fe-tracking-info-card">
              <h3 class="fe-tracking-info-card__title">
                <i class="fas fa-map-marker-alt"></i> Shipping Address
              </h3>
              <div class="fe-tracking-info-card__address">
                <p v-if="trackingData.shipping_address.name">
                  <strong>{{ trackingData.shipping_address.name }}</strong>
                </p>
                <p v-if="trackingData.shipping_address.phone">
                  <i class="fas fa-phone-alt"></i> {{ trackingData.shipping_address.phone }}
                </p>
                <p v-if="trackingData.shipping_address.address">
                  {{ trackingData.shipping_address.address }}
                </p>
                <p v-if="trackingData.shipping_address.city || trackingData.shipping_address.zip_code">
                  {{ trackingData.shipping_address.city }}
                  <span v-if="trackingData.shipping_address.zip_code">, {{ trackingData.shipping_address.zip_code }}</span>
                </p>
              </div>
            </div>

            <div v-if="trackingData.package" class="fe-tracking-info-card">
              <h3 class="fe-tracking-info-card__title">
                <i class="fas fa-box"></i> Package Information
              </h3>
              <div class="fe-tracking-info-card__grid">
                <div v-if="trackingData.package.package_no" class="fe-tracking-info-card__item">
                  <span class="fe-tracking-info-card__label">Package No</span>
                  <span class="fe-tracking-info-card__value">{{ trackingData.package.package_no }}</span>
                </div>
                <div class="fe-tracking-info-card__item">
                  <span class="fe-tracking-info-card__label">Package Status</span>
                  <span class="fe-tracking-info-card__value">{{ trackingData.order.package_status }}</span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="isLoggedIn" class="fe-tracking-actions">
            <router-link
              :to="'/order/' + trackingData.order.id"
              class="fe-tracking-action-btn fe-tracking-action-btn--primary"
            >
              <i class="fas fa-eye"></i> View Full Order Details
            </router-link>
            <router-link
              to="/my-orders"
              class="fe-tracking-action-btn"
            >
              <i class="fas fa-list"></i> All Orders
            </router-link>
          </div>
        </div>

        <div v-if="!isLoggedIn && !trackingData && !error && !loading" class="fe-tracking-help">
          <div class="fe-tracking-help__card">
            <div class="fe-tracking-help__icon"><i class="fas fa-info-circle"></i></div>
            <h3>How to find your order reference?</h3>
            <ul>
              <li>Check your order confirmation email</li>
              <li>The reference starts with <strong>ORD-</strong> followed by a unique code</li>
              <li><router-link to="/login">Sign in</router-link> to view all your orders with tracking</li>
            </ul>
          </div>
          <div class="fe-tracking-help__card">
            <div class="fe-tracking-help__icon"><i class="fas fa-headset"></i></div>
            <h3>Need help?</h3>
            <p>
              If you're having trouble tracking your order, please
              <router-link to="/contact">contact our support team</router-link>.
            </p>
          </div>
        </div>

          </div>
        </div>
      </div>
    </div>

    <cta-area />
  </div>
</template>

<script>
import CtaArea from '@/components/cta/cta-area.vue'
import AccountSidebar from '@/components/account/AccountSidebar.vue'

export default {
  name: 'OrderTracking',
  components: { CtaArea, AccountSidebar },
  data() {
    return {
      searchRef: '',
      loading: false,
      error: null,
      trackingData: null,
      userOrders: [],
      userOrdersLoading: false,
      user: null,
    }
  },
  computed: {
    isLoggedIn() {
      return !!this.user
    },
  },
  mounted() {
    try {
      this.user = JSON.parse(localStorage.getItem('user')) || null
    } catch {
      this.user = null
    }

    if (this.$route.query.ref) {
      this.searchRef = this.$route.query.ref
      this.handleTrack()
    } else if (this.$route.query.id) {
      this.trackById(this.$route.query.id)
    } else if (this.isLoggedIn) {
      this.fetchUserOrders()
    }
  },
  methods: {
    async handleTrack() {
      const ref = this.searchRef.trim()
      if (!ref) return

      this.loading = true
      this.error = null
      this.trackingData = null

      try {
        const { data } = await this.$axios.get('/track-order', {
          params: { reference: ref },
        })
        this.trackingData = data
      } catch (err) {
        const msg = err.response?.data?.error
        this.error = msg || 'Unable to track order. Please try again.'
      } finally {
        this.loading = false
      }
    },

    async trackById(id) {
      this.loading = true
      this.error = null
      this.trackingData = null

      try {
        const { data } = await this.$axios.get('/track-order', {
          params: { order_id: id },
        })
        this.trackingData = data
      } catch (err) {
        const msg = err.response?.data?.error
        this.error = msg || 'Unable to track order. Please try again.'
      } finally {
        this.loading = false
      }
    },

    async fetchUserOrders() {
      if (!this.user?.id) return
      this.userOrdersLoading = true
      try {
        const { data } = await this.$axios.get(`/track-order/user/${this.user.id}`)
        this.userOrders = Array.isArray(data) ? data : []
      } catch {
        this.userOrders = []
      } finally {
        this.userOrdersLoading = false
      }
    },

    selectOrder(o) {
      this.trackingData = o
      this.error = null
      this.searchRef = o.order.reference
    },

    clearTracking() {
      this.trackingData = null
      this.error = null
      this.searchRef = ''
    },

    formatDate(dateStr) {
      if (!dateStr) return '—'
      const d = new Date(dateStr)
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '—'
      const d = new Date(dateStr)
      return d.toLocaleDateString(undefined, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    },

    formatMoney(val) {
      if (val === null || val === undefined || val === '') return '—'
      const n = Number(val)
      if (Number.isNaN(n)) return String(val)
      return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    getStatusClass(step, timeline) {
      const hasFailed = timeline.some(t => t.status === 'failed')
      if (hasFailed) return 'is-failed'
      if (step >= 5) return 'is-delivered'
      if (step >= 3) return 'is-progress'
      return 'is-pending'
    },

    getCurrentStatusLabel(step, timeline) {
      const hasFailed = timeline.some(t => t.status === 'failed')
      if (hasFailed) return 'Rejected'
      const active = timeline.find(t => t.status === 'in_progress')
      if (active) return active.title
      const lastCompleted = [...timeline].reverse().find(t => t.status === 'completed')
      return lastCompleted ? lastCompleted.title : 'Pending'
    },

    getMiniProgressWidth(o) {
      const hasFailed = o.timeline.some(t => t.status === 'failed')
      if (hasFailed) return '25%'
      return `${((o.current_step) / 5) * 100}%`
    },

    getMainStatusClass() {
      if (!this.trackingData) return ''
      const hasFailed = this.trackingData.timeline.some(t => t.status === 'failed')
      if (hasFailed) return 'fe-tracking-badge--danger'
      const step = this.trackingData.current_step
      if (step >= 5) return 'fe-tracking-badge--success'
      if (step >= 3) return 'fe-tracking-badge--info'
      if (step >= 2) return 'fe-tracking-badge--warn'
      return 'fe-tracking-badge--neutral'
    },

    getMainStatusIcon() {
      if (!this.trackingData) return 'fa-circle'
      const hasFailed = this.trackingData.timeline.some(t => t.status === 'failed')
      if (hasFailed) return 'fa-times-circle'
      const step = this.trackingData.current_step
      if (step >= 5) return 'fa-check-double'
      if (step >= 4) return 'fa-truck'
      if (step >= 3) return 'fa-box'
      if (step >= 2) return 'fa-check-circle'
      return 'fa-clock'
    },

    getMainStatusText() {
      if (!this.trackingData) return ''
      return this.getCurrentStatusLabel(
        this.trackingData.current_step,
        this.trackingData.timeline
      )
    },
  },
}
</script>

<style scoped>
.fe-tracking-page {
  background: linear-gradient(180deg, #f4f6f9 0%, #ffffff 300px);
  min-height: 70vh;
}

/* ─── Header ─── */
.fe-tracking-page__header {
  text-align: center;
  margin-bottom: 2rem;
  padding-top: 1rem;
}

.fe-tracking-page__icon-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--fe-accent, #fcbe00) 0%, #f59e0b 100%);
  color: #fff;
  font-size: 28px;
  margin-bottom: 1rem;
  box-shadow: 0 8px 28px rgba(252, 190, 0, 0.3);
}

.fe-tracking-page__title {
  font-size: clamp(1.6rem, 4vw, 2.2rem);
  font-weight: 800;
  letter-spacing: -0.03em;
  color: #0f172a;
  margin: 0 0 0.5rem;
}

.fe-tracking-page__subtitle {
  margin: 0 auto;
  max-width: 560px;
  font-size: 1rem;
  color: #64748b;
  line-height: 1.6;
}

/* ─── Search Card ─── */
.fe-tracking-search-card {
  max-width: 680px;
  margin: 0 auto 2.5rem;
  background: #fff;
  border-radius: 18px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 8px 32px rgba(15, 23, 42, 0.06);
  padding: 1.5rem;
}

.fe-tracking-search {
  display: flex;
  gap: 0.75rem;
  align-items: stretch;
}

.fe-tracking-search__input-wrap {
  flex: 1;
  position: relative;
}

.fe-tracking-search__icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  font-size: 16px;
}

.fe-tracking-search__input {
  width: 100%;
  height: 52px;
  padding: 0 16px 0 44px;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 500;
  color: #0f172a;
  background: #f8fafc;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
  outline: none;
}

.fe-tracking-search__input:focus {
  border-color: var(--fe-accent, #fcbe00);
  background: #fff;
  box-shadow: 0 0 0 3px rgba(252, 190, 0, 0.15);
}

.fe-tracking-search__input::placeholder {
  color: #94a3b8;
  font-weight: 400;
}

.fe-tracking-search__btn {
  height: 52px;
  padding: 0 28px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, var(--fe-accent, #fcbe00) 0%, #f59e0b 100%);
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
  transition: transform 0.15s ease, box-shadow 0.2s ease, filter 0.2s ease;
  box-shadow: 0 4px 14px rgba(252, 190, 0, 0.3);
}

.fe-tracking-search__btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(252, 190, 0, 0.4);
  filter: brightness(1.05);
}

.fe-tracking-search__btn:active:not(:disabled) {
  transform: scale(0.98);
}

.fe-tracking-search__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.fe-tracking-search__spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: fe-spin 0.7s linear infinite;
}

/* ─── User Orders Grid ─── */
.fe-tracking-user-orders {
  max-width: 900px;
  margin: 0 auto 2rem;
}

.fe-tracking-user-orders__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}

.fe-tracking-user-orders__title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 800;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.fe-tracking-user-orders__title i {
  color: var(--fe-accent, #fcbe00);
}

.fe-tracking-user-orders__count {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding: 0.3rem 0.75rem;
  border-radius: 999px;
  background: color-mix(in srgb, var(--fe-accent, #fcbe00) 14%, #fff);
  color: #92400e;
}

.fe-tracking-orders-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1rem;
}

/* ─── Order Card ─── */
.fe-tracking-order-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 1.25rem;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.fe-tracking-order-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 36px rgba(15, 23, 42, 0.08);
  border-color: var(--fe-accent, #fcbe00);
}

.fe-tracking-order-card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.85rem;
}

.fe-tracking-order-card__ref {
  display: block;
  font-size: 0.9rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.01em;
}

.fe-tracking-order-card__date {
  display: block;
  font-size: 0.78rem;
  color: #64748b;
  margin-top: 2px;
}

.fe-tracking-order-card__status {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  white-space: nowrap;
}

.fe-tracking-order-card__status.is-delivered {
  background: #dcfce7;
  color: #166534;
}

.fe-tracking-order-card__status.is-progress {
  background: #dbeafe;
  color: #1e40af;
}

.fe-tracking-order-card__status.is-pending {
  background: #fef3c7;
  color: #92400e;
}

.fe-tracking-order-card__status.is-failed {
  background: #fee2e2;
  color: #991b1b;
}

/* Mini progress bar */
.fe-tracking-order-card__progress {
  margin-bottom: 0.85rem;
}

.fe-tracking-mini-progress {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 8px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
}

.fe-tracking-mini-progress__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #cbd5e1;
  z-index: 2;
  transition: background 0.3s ease;
}

.fe-tracking-mini-progress__dot.is-completed {
  background: #22c55e;
}

.fe-tracking-mini-progress__dot.is-failed {
  background: #ef4444;
}

.fe-tracking-mini-progress__bar {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: linear-gradient(90deg, #22c55e 0%, #16a34a 100%);
  border-radius: 999px;
  transition: width 0.5s ease;
}

.fe-tracking-order-card__bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.fe-tracking-order-card__total {
  font-size: 0.95rem;
  font-weight: 800;
  color: #0f172a;
  font-variant-numeric: tabular-nums;
}

.fe-tracking-order-card__arrow {
  color: #94a3b8;
  font-size: 0.85rem;
  transition: transform 0.2s ease, color 0.2s ease;
}

.fe-tracking-order-card:hover .fe-tracking-order-card__arrow {
  transform: translateX(3px);
  color: var(--fe-accent, #fcbe00);
}

/* ─── Error ─── */
.fe-tracking-error {
  max-width: 600px;
  margin: 0 auto 2rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-radius: 16px;
  background: #fff5f5;
  border: 1px solid #fecaca;
}

.fe-tracking-error__icon {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #fee2e2;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #dc2626;
  font-size: 20px;
}

.fe-tracking-error__title {
  margin: 0 0 0.25rem;
  font-size: 1rem;
  font-weight: 800;
  color: #991b1b;
}

.fe-tracking-error__text {
  margin: 0;
  font-size: 0.9rem;
  color: #b91c1c;
  line-height: 1.5;
}

/* ─── Tracking Result ─── */
.fe-tracking-result {
  max-width: 900px;
  margin: 0 auto;
}

.fe-tracking-back-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  color: #475569;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 1.25rem;
  transition: all 0.2s ease;
}

.fe-tracking-back-btn:hover {
  border-color: var(--fe-accent, #fcbe00);
  color: #0f172a;
  background: color-mix(in srgb, var(--fe-accent, #fcbe00) 8%, #fff);
}

/* Result Header */
.fe-tracking-result__header {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 1.5rem 1.75rem;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.25rem;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.05);
}

.fe-tracking-result__label {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  display: block;
  margin-bottom: 0.25rem;
}

.fe-tracking-result__ref {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
  word-break: break-all;
}

.fe-tracking-result__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
  margin-top: 0.75rem;
}

.fe-tracking-result__meta-item {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.88rem;
  color: #475569;
  font-weight: 500;
}

.fe-tracking-result__meta-item i {
  color: #94a3b8;
  font-size: 0.85rem;
}

/* Status Badge */
.fe-tracking-result__status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.55rem 1.15rem;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}

.fe-tracking-badge--success {
  background: #dcfce7;
  color: #166534;
}

.fe-tracking-badge--info {
  background: #dbeafe;
  color: #1e40af;
}

.fe-tracking-badge--warn {
  background: #fef3c7;
  color: #92400e;
}

.fe-tracking-badge--danger {
  background: #fee2e2;
  color: #991b1b;
}

.fe-tracking-badge--neutral {
  background: #f1f5f9;
  color: #475569;
}

/* ─── Timeline ─── */
.fe-tracking-timeline-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 1.5rem 1.75rem;
  margin-bottom: 1.25rem;
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.05);
}

.fe-tracking-timeline-card__title {
  margin: 0 0 1.5rem;
  font-size: 1.05rem;
  font-weight: 800;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.fe-tracking-timeline-card__title i {
  color: var(--fe-accent, #fcbe00);
}

.fe-tracking-timeline {
  position: relative;
}

.fe-tracking-timeline__step {
  display: flex;
  gap: 1.25rem;
  position: relative;
  padding-bottom: 0;
}

.fe-tracking-timeline__step:last-child {
  padding-bottom: 0;
}

.fe-tracking-timeline__connector {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  width: 40px;
}

.fe-tracking-timeline__line {
  width: 3px;
  flex: 1;
  min-height: 20px;
  background: #e2e8f0;
  border-radius: 999px;
  transition: background 0.3s ease;
}

.fe-tracking-timeline__line.is-filled {
  background: linear-gradient(180deg, #22c55e 0%, #16a34a 100%);
}

.fe-tracking-timeline__line--bottom {
  min-height: 24px;
}

.fe-tracking-timeline__dot {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  flex-shrink: 0;
  transition: all 0.3s ease;
  background: #e2e8f0;
  color: #94a3b8;
  border: 3px solid #fff;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.08);
}

.fe-tracking-timeline__dot-num {
  font-size: 13px;
  font-weight: 800;
}

.fe-tracking-timeline__step.is-completed .fe-tracking-timeline__dot {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  color: #fff;
  box-shadow: 0 4px 14px rgba(34, 197, 94, 0.3);
}

.fe-tracking-timeline__step.is-active .fe-tracking-timeline__dot {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: #fff;
  box-shadow: 0 4px 14px rgba(59, 130, 246, 0.3);
  animation: fe-pulse-dot 2s ease-in-out infinite;
}

.fe-tracking-timeline__step.is-failed .fe-tracking-timeline__dot {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: #fff;
  box-shadow: 0 4px 14px rgba(239, 68, 68, 0.3);
}

@keyframes fe-pulse-dot {
  0%, 100% { box-shadow: 0 4px 14px rgba(59, 130, 246, 0.3); }
  50% { box-shadow: 0 4px 24px rgba(59, 130, 246, 0.5); }
}

.fe-tracking-timeline__content {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  padding-bottom: 1.5rem;
  flex: 1;
}

.fe-tracking-timeline__step:last-child .fe-tracking-timeline__content {
  padding-bottom: 0;
}

.fe-tracking-timeline__icon-bg {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  flex-shrink: 0;
  background: #f1f5f9;
  color: #94a3b8;
  transition: all 0.3s ease;
}

.fe-tracking-timeline__step.is-completed .fe-tracking-timeline__icon-bg {
  background: #f0fdf4;
  color: #16a34a;
}

.fe-tracking-timeline__step.is-active .fe-tracking-timeline__icon-bg {
  background: #eff6ff;
  color: #2563eb;
}

.fe-tracking-timeline__step.is-failed .fe-tracking-timeline__icon-bg {
  background: #fef2f2;
  color: #dc2626;
}

.fe-tracking-timeline__text {
  min-width: 0;
  flex: 1;
}

.fe-tracking-timeline__title {
  margin: 0 0 0.2rem;
  font-size: 0.95rem;
  font-weight: 800;
  color: #0f172a;
}

.fe-tracking-timeline__step.is-pending .fe-tracking-timeline__title {
  color: #94a3b8;
}

.fe-tracking-timeline__desc {
  margin: 0;
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.5;
}

.fe-tracking-timeline__step.is-pending .fe-tracking-timeline__desc {
  color: #cbd5e1;
}

.fe-tracking-timeline__date {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-top: 0.4rem;
  font-size: 0.78rem;
  color: #94a3b8;
  font-weight: 500;
}

/* ─── Info Cards ─── */
.fe-tracking-details-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.fe-tracking-info-card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 1.25rem 1.35rem;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.04);
}

.fe-tracking-info-card__title {
  margin: 0 0 1rem;
  font-size: 0.92rem;
  font-weight: 800;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f1f5f9;
}

.fe-tracking-info-card__title i {
  color: var(--fe-accent, #fcbe00);
  font-size: 0.95rem;
}

.fe-tracking-info-card__grid {
  display: grid;
  gap: 0.75rem;
}

.fe-tracking-info-card__item {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
}

.fe-tracking-info-card__label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #94a3b8;
}

.fe-tracking-info-card__value {
  font-size: 0.88rem;
  font-weight: 600;
  color: #0f172a;
  text-align: right;
}

.fe-tracking-info-card__value--status {
  padding: 0.15rem 0.5rem;
  border-radius: 6px;
  background: #f0fdf4;
  color: #16a34a;
  font-size: 0.78rem;
  font-weight: 700;
}

.fe-tracking-info-card__address {
  font-size: 0.88rem;
  color: #334155;
  line-height: 1.7;
}

.fe-tracking-info-card__address p {
  margin: 0;
}

.fe-tracking-info-card__address i {
  color: #94a3b8;
  font-size: 0.8rem;
  margin-right: 0.25rem;
}

/* ─── Actions ─── */
.fe-tracking-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 2rem;
}

.fe-tracking-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.7rem 1.35rem;
  border-radius: 12px;
  font-size: 0.88rem;
  font-weight: 700;
  text-decoration: none;
  color: #0f172a;
  border: 1px solid #e2e8f0;
  background: #fff;
  transition: all 0.2s ease;
}

.fe-tracking-action-btn:hover {
  border-color: var(--fe-accent, #fcbe00);
  background: color-mix(in srgb, var(--fe-accent, #fcbe00) 8%, #fff);
  color: #0f172a;
}

.fe-tracking-action-btn--primary {
  background: linear-gradient(135deg, var(--fe-accent, #fcbe00) 0%, #f59e0b 100%);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 4px 14px rgba(252, 190, 0, 0.3);
}

.fe-tracking-action-btn--primary:hover {
  filter: brightness(1.05);
  box-shadow: 0 6px 20px rgba(252, 190, 0, 0.4);
  color: #fff;
  background: linear-gradient(135deg, var(--fe-accent, #fcbe00) 0%, #f59e0b 100%);
}

/* ─── Help Section ─── */
.fe-tracking-help {
  max-width: 700px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
}

.fe-tracking-help__card {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 1.5rem;
}

.fe-tracking-help__icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  margin-bottom: 1rem;
  background: color-mix(in srgb, var(--fe-accent, #fcbe00) 12%, #fff);
  color: var(--fe-accent, #d4a000);
}

.fe-tracking-help__card h3 {
  margin: 0 0 0.75rem;
  font-size: 0.95rem;
  font-weight: 800;
  color: #0f172a;
}

.fe-tracking-help__card ul {
  margin: 0;
  padding: 0 0 0 1.25rem;
  font-size: 0.88rem;
  color: #475569;
  line-height: 1.8;
}

.fe-tracking-help__card p {
  margin: 0;
  font-size: 0.88rem;
  color: #475569;
  line-height: 1.6;
}

.fe-tracking-help__card a {
  color: var(--fe-accent, #d4a000);
  font-weight: 700;
  text-decoration: none;
}

.fe-tracking-help__card a:hover {
  text-decoration: underline;
}

/* ─── States ─── */
.fe-tracking-state {
  text-align: center;
  padding: 3rem 1rem;
  color: #64748b;
}

.fe-tracking-spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 1rem;
  border: 3px solid #e2e8f0;
  border-top-color: var(--fe-accent, #fcbe00);
  border-radius: 50%;
  animation: fe-spin 0.85s linear infinite;
}

.fe-tracking-empty {
  text-align: center;
  padding: 3rem 1rem;
  color: #94a3b8;
}

.fe-tracking-empty i {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #cbd5e1;
}

.fe-tracking-empty p {
  margin: 0 0 1rem;
  font-size: 1rem;
  color: #64748b;
}

.fe-tracking-empty__link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.25rem;
  border-radius: 10px;
  background: var(--fe-accent, #fcbe00);
  color: #fff;
  font-weight: 700;
  text-decoration: none;
  font-size: 0.9rem;
}

@keyframes fe-spin {
  to { transform: rotate(360deg); }
}

/* ─── Responsive ─── */
@media (max-width: 639px) {
  .fe-tracking-search {
    flex-direction: column;
  }

  .fe-tracking-search__btn {
    width: 100%;
  }

  .fe-tracking-result__header {
    flex-direction: column;
  }

  .fe-tracking-details-row {
    grid-template-columns: 1fr;
  }

  .fe-tracking-orders-grid {
    grid-template-columns: 1fr;
  }

  .fe-tracking-timeline__content {
    flex-direction: column;
    gap: 0.5rem;
  }
}
</style>
